import React, { useEffect } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import toast from 'react-hot-toast';
import {
  LayoutDashboard, Palmtree, ClipboardList, User, Users, Briefcase,
  Building2, Shield, Info, LogOut, Calendar, FileText, CalendarDays,
} from 'lucide-react';

const links = {
  employee: [
    { to: '/dashboard',  icon: LayoutDashboard, label: 'Panel' },
    { to: '/vacaciones', icon: Palmtree,         label: 'Mis vacaciones' },
    { to: '/permisos',   icon: ClipboardList,    label: 'Mis permisos' },
    { to: '/perfil',     icon: User,             label: 'Mi perfil' },
  ],
  supervisor: [
    { to: '/supervisor',                  icon: LayoutDashboard, label: 'Panel' },
    { to: '/supervisor/mis-vacaciones',   icon: Palmtree,        label: 'Mis vacaciones' },
    { to: '/supervisor/mis-permisos',     icon: ClipboardList,   label: 'Mis permisos' },
    { to: '/supervisor/informes',         icon: FileText,        label: 'Informes' },
    { to: '/supervisor/perfil',           icon: User,            label: 'Mi perfil' },
  ],
  admin: [
    { to: '/admin',                  icon: LayoutDashboard, label: 'Panel' },
    { to: '/admin/empleados',        icon: Users,           label: 'Empleados' },
    { to: '/admin/supervisores',     icon: Briefcase,       label: 'Supervisores' },
    { to: '/admin/departamentos',    icon: Building2,       label: 'Departamentos' },
    { to: '/admin/vacaciones',       icon: Palmtree,        label: 'Vacaciones' },
    { to: '/admin/acumulaciones',    icon: Calendar,        label: 'Acumulaciones' },
    { to: '/admin/feriados',         icon: CalendarDays,    label: 'Feriados' },
    { to: '/admin/informes',         icon: FileText,        label: 'Informes' },
    { to: '/admin/auditoria',        icon: Shield,          label: 'Auditoría' },
  ],
};

const roleLabels = { employee: 'Empleado', supervisor: 'Supervisor', admin: 'Administrador' };

export default function Sidebar({ open, onClose }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Cerrar el sidebar al cambiar de ruta en mobile
  useEffect(() => {
    onClose?.();
  }, [location.pathname]);

  // Cerrar con Escape
  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') onClose?.(); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, []);

  const handleLogout = () => {
    logout();
    toast.success('Sesión cerrada');
    navigate('/login');
  };

  const navLinks = links[user?.role] || [];
  const initials = user?.name
    ? user.name.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase()
    : '?';

  return (
    <>
      {/* Overlay para cerrar el sidebar en mobile */}
      <div
        className={`sidebar-overlay ${open ? 'visible' : ''}`}
        onClick={onClose}
        aria-hidden="true"
      />

      <aside className={`sidebar ${open ? 'open' : ''}`}>
        <div className="sidebar-logo">
          <img
            src="/logo-talento.png"
            alt="Logo Sistema Talento"
            style={{ width: 190, height: 40, borderRadius: 18, marginBottom: 2 }}
          />
        </div>

        <nav className="sidebar-nav">
          <div className="sidebar-section-label">Menú principal</div>
          {navLinks.map(link => {
            const Icon = link.icon;
            return (
              <NavLink
                key={link.to}
                to={link.to}
                end={link.to.split('/').length <= 2}
                className={({ isActive }) => `sidebar-link${isActive ? ' active' : ''}`}
              >
                <Icon size={17} strokeWidth={1.8} />
                {link.label}
              </NavLink>
            );
          })}
        </nav>

        <div className="sidebar-footer">
          <div className="sidebar-user">
            <div className="sidebar-avatar">{initials}</div>
            <div className="sidebar-user-info">
              <div className="sidebar-user-name">{user?.name || 'Usuario'}</div>
              <div className="sidebar-user-role">{roleLabels[user?.role] || ''}</div>
            </div>
            <button className="logout-btn" onClick={handleLogout} title="Cerrar sesión">
              <LogOut size={16} />
            </button>
          </div>
        </div>
      </aside>
    </>
  );
}
